/**
 * `reportOrientWrapperBytes` — extracted out of orient.ts (STD-2/DEC-1504: that file is
 * grandfathered shrink-only, so WP-582 B2's growth there needs an equal-or-greater offsetting
 * extraction in the SAME PR). Pure fire-and-forget observability helper, module-private in its
 * original home.
 */
import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { kernelMutation, getAgentSessionId, getConversationId } from "../client.js";

/**
 * WP-376 α.3: measure assembled MCP tool output bytes for orient and report
 * fire-and-forget to the gateway. The number captured here is the byte size
 * of the full MCP `tool_result` payload (content array JSON-serialized) — the
 * surface-side counterpart to the gateway's envelope-bytes measurement.
 *
 * Errors are swallowed: observability must never block the orient response or
 * affect the user-visible output. The gateway resolves workspaceId / apiKeyId
 * from the API-key context (BR-18 + injectSessionAuth).
 *
 * Skipped when `truncated` cannot be determined AND no orient view returned
 * (e.g. the early `Already oriented` return path with no orient data).
 *
 * DEC-NEW: also reports the resolved tier + task-presence for observability.
 */
export function reportOrientWrapperBytes(
  toolResult: { content: Array<{ type: string; text: string }>; structuredContent?: unknown },
  truncated: boolean,
  tier: "summary" | "standard" | "full",
  taskProvided: boolean,
): void {
  try {
    const fullToolOutput = JSON.stringify({ content: toolResult.content ?? [] });
    const bytes = Buffer.byteLength(fullToolOutput, "utf8");
    // Thread the active session context (Codex re-review) so the server's N-per-key ambiguity guard doesn't drop this rendered-byte report.
    const sessionId = getAgentSessionId();
    const conversationId = getConversationId();
    void kernelMutation<GatewayRouteReturnByName<"agent.reportOrientMetric">>("agent.reportOrientMetric", {
      bytes,
      truncated,
      tier,
      taskProvided,
      ...(sessionId ? { sessionId } : {}),
      ...(conversationId ? { conversationId } : {}),
    }).catch(() => {
      // Best-effort observability — silently swallow.
    });
  } catch {
    // Even building the byte count failed (e.g. circular structuredContent in
    // an unexpected shape) — never let it surface to the caller.
  }
}
